<template>
  <div class="pagination" v-if="pageLen > 1">
    <div class="flex-row p-view">
      <div class="mo-paging flex-row">
        <a ref="left" class="paging-item paging-item--prev" @click="leftArrow($event)">
          <img src="@/assets/image/pagination-left-arrow.png" alt="" class="arrow" />
        </a>
        <template v-for="(item, index) of pagers" :key="index">
          <div v-if="item.isMore && pageLen > 4" class="paging-item paging-item--more">
            ...
          </div>
          <a v-else :class="item.class + ' paging-item'" @click="pageEvent(item)">{{
            item.index
          }}</a>
        </template>
        <a ref="right" class="paging-item paging-item--next" @click="rightArrow($event)">
          <img src="@/assets/image/pagination-right-arrow.png" alt="" class="arrow" />
        </a>
      </div>
      <template>
        <span class="desc tz">跳至</span>
        <input
          class="input"
          type="text"
          v-model="jumpToNum"
          oninput="value=value.replace(/[^\d]/g,'')"
          maxlength="3"
          @blur="handleBlur"
        />
        <span class="desc ye">页</span>
        <div class="sure" @click="confirm">确定</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineEmits, computed, ref, shallowRef } from "vue";
import { paginationProps, paginationEmit } from "./pagination";

type pageItem = { index: number; isMore: boolean; isCurrent: boolean; class: string };

const props = defineProps(paginationProps);
const emit = defineEmits(paginationEmit);

const currentPage = ref(props.current);
const pageLen = computed(() => {
  return Math.ceil(props.total / props.length);
});
const jumpToNum = ref();

const left = shallowRef<HTMLInputElement>();
const right = shallowRef<HTMLInputElement>();
const _refLeft = computed(() => left.value);
const _refRight = computed(() => right.value);
/**
 * 3种形态
 *  1. 无省略号，len <= 4，current = 1 ~ 4
 *  2. 有一个省略号，page > 4，current = 1 | 2 | len - 1 | len
 *  3. 两个省略号，page > 4，len - current >= 2，current - 1 >= 2
 */
const pagers = computed(() => {
  const arr: pageItem[] = [];

  // 从current位置向左扩散
  for (let i = currentPage.value; i >= 1; i--) {
    // 终结条件：
    if (i === currentPage.value - 4 || i === 0) {
      break;
    }
    const isOne = i === currentPage.value - 3 && i > 0;
    arr.unshift({
      index: isOne ? 1 : i,
      isMore: i === currentPage.value - 2,
      isCurrent: i === currentPage.value,
      class: i === currentPage.value ? "paging-item--current" : "",
    });
  }
  // 从current向右扩散
  for (let i = currentPage.value + 1; i <= pageLen.value; i++) {
    // 终结条件：超出最大长度或者还有更多
    if (i === currentPage.value + 4 || i === pageLen.value + 1) {
      break;
    }
    const isLast = i === currentPage.value + 3 && i < pageLen.value;
    arr.push({
      index: isLast ? pageLen.value : i,
      isMore: i === currentPage.value + 2,
      isCurrent: i === currentPage.value,
      class: i === currentPage.value ? "paging-item--current" : "",
    });
  }
  // 特别情况处理(为了在选择最开始/最末尾的时候有3个)
  if (
    pageLen.value > 4 &&
    (currentPage.value === 1 || currentPage.value === pageLen.value)
  ) {
    arr.splice(2, 0, {
      index: currentPage.value === 1 ? 3 : pageLen.value - 2,
      isMore: false,
      isCurrent: false,
      class: "",
    });
  }

  return arr;
});

const pageEvent = (item: pageItem) => {
  if (item.isCurrent) {
    return;
  }
  currentPage.value = item.index;
  emit("change", currentPage.value);
};

const leftArrow = (e: MouseEvent) => {
  const currentTarget = e.currentTarget as HTMLDivElement;
  if (currentPage.value === 1) {
    // 设置左箭头非活性
    if (!currentTarget.getAttribute("class")?.includes("paging-item--disabled")) {
      currentTarget.setAttribute(
        "class",
        currentTarget.getAttribute("class") + " paging-item--disabled"
      );
    }
    return;
  }
  // 移除右箭头非活性
  if (_refRight.value?.getAttribute("class")?.includes("paging-item--disabled")) {
    _refRight.value.setAttribute("class", currentTarget.getAttribute("class") || "");
  }
  currentPage.value -= 1;
  emit("change", currentPage.value);
};

const rightArrow = (e: MouseEvent) => {
  const currentTarget = e.currentTarget as HTMLDivElement;
  if (currentPage.value === pageLen.value) {
    // 设置右箭头非活性
    if (currentTarget.getAttribute("class")?.includes("paging-item--disabled")) {
      currentTarget.setAttribute(
        "class",
        currentTarget.getAttribute("class") + " paging-item--disabled"
      );
    }
    return;
  }
  // 移除右箭头非活性
  if (_refLeft.value?.getAttribute("class")?.includes("paging-item--disabled")) {
    _refLeft.value.setAttribute("class", currentTarget.getAttribute("class") || "");
  }
  // 移除左箭头非活性
  currentPage.value += 1;
  emit("change", currentPage.value);
};

const handleBlur = () => {
  if (jumpToNum.value > pageLen.value) {
    jumpToNum.value = pageLen.value;
  }
  if (jumpToNum.value < 1) {
    jumpToNum.value = 1;
  }
};

const confirm = () => {
  if (jumpToNum.value === currentPage.value || !jumpToNum.value) {
    return;
  }

  currentPage.value = Number(jumpToNum.value);
  jumpToNum.value = null;
  emit("change", currentPage.value);
};
</script>

<style lang="less" scoped>
.pagination {
  margin: 48px 0 82px;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.p-view {
  align-items: center;
  justify-content: center;

  .tz {
    margin: 0 16px 0 38px;
  }

  .input {
    height: 40px;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    width: 39px;
    background: #fff;
    border: 1px solid #e2e2e2;
    font-family: dm;
    color: #6f6f6f;
    line-height: 21px;
  }

  .ye {
    margin: 0 36px 0 17px;
  }

  .desc {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    line-height: 28px;
    white-space: nowrap;
  }

  .sure {
    background: #000;
    padding: 7px 12px;
    color: hsla(0, 0%, 100%, 0.6);
    line-height: 25px;
    box-sizing: border-box;
    letter-spacing: 2px;
    white-space: nowrap;
    cursor: pointer;
    height: 40px;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
  }
}

.mo-paging {
  padding: 0;
  margin: 1rem 0;
  font-size: 0;
  list-style: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  .paging-item {
    display: inline;
    font-size: 14px;
    position: relative;
    width: 39px;
    height: 40px;
    text-decoration: none;
    border: 1px solid #e2e2e2;
    background-color: #fff;
    margin-left: 16px;
    font-size: 18px;
    font-family: dm;
    font-weight: 700;
    color: #6f6f6f;
    line-height: 21px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .paging-item--current {
    cursor: default;
    background-color: #A9823E;
    color: #fff;
    position: relative;
    z-index: 1;
    border-color: #A9823E;
  }

  .paging-item--disabled {
    cursor: not-allowed;
    opacity: 0.75;
    background-color: #fff;
    color: #505050;
  }

  .paging-item-more {
    cursor: default;
    background-color: #fff;
    color: #505050;
  }

  .arrow {
    cursor: pointer;
    width: 7px;
    height: 11px;
  }
}

.desc {
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  line-height: 28px;
  white-space: nowrap;
}
</style>
